import { createSlice, createSelector } from "@reduxjs/toolkit";

// --- helpers ---
const toTitle = (s = "") => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const nameFromEmail = (email = "") =>
  email
    .split("@")[0]
    .split(/[._+-]/)
    .filter(Boolean)
    .map(toTitle)
    .join(" ");

const pick = (...vals) =>
  vals.find((v) => typeof v === "string" && v.trim())?.trim() || "";

// If "first" already ends with "last" (e.g. "Abdul Rehman Shahid" + "Shahid"),
// don't append last again.
const joinNameSmart = (first = "", last = "") => {
  const f = first.trim();
  const l = last.trim();
  if (!f && !l) return "";
  if (!f) return l;
  if (!l) return f;

  const fTokens = f.split(/\s+/);
  const lTokens = l.split(/\s+/);
  const fTail = fTokens.slice(-lTokens.length).join(" ").toLowerCase();
  const lJoin = lTokens.join(" ").toLowerCase();

  return fTail === lJoin ? f : `${f} ${l}`;
};

// Collapse adjacent duplicate words: "Shahid Shahid" -> "Shahid"
const dedupeAdjacentWords = (str = "") => {
  const parts = str.split(/\s+/).filter(Boolean);
  const out = [];
  for (const p of parts) {
    if (!out.length || out[out.length - 1].toLowerCase() !== p.toLowerCase()) {
      out.push(p);
    }
  }
  return out.join(" ");
};

const normalizeUser = (raw = {}) => {
  const email = pick(raw.email, raw.EmailId, raw.Email);

  const first = pick(raw.FirstName, raw.firstName, raw.given_name);
  const last = pick(raw.LastName, raw.lastName, raw.family_name);

  // Prefer a provided combined name; otherwise join smartly to avoid duplicates.
  const providedCombined = pick(raw.Name, raw.name, raw.displayName);
  let displayName = providedCombined || joinNameSmart(first, last);

  // Fallbacks: derive from email, or "Account"
  displayName = displayName || nameFromEmail(email) || "Account";

  // Clean up accidental adjacent repeats ("Shahid Shahid")
  displayName = dedupeAdjacentWords(displayName);

  const avatar = pick(
    raw.AuthProviderImg,
    raw.authProviderImg,
    raw.AuthProviderImage,
    raw.authProviderImage,
    raw.picture,
    raw.avatar,
    raw.avatarUrl
  );

  return {
    ...raw,
    email,
    displayName,
    avatar,
  };
};

const initialState = {
  user: null,
  isLoggedIn: false,
  isError: false,
  isSuccess: false,
  isLoading: false,
  twoFactor: false,
  message: "",
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    reset(state) {
      state.isError = false;
      state.isSuccess = false;
      state.isLoading = false;
      state.message = "";
    },
    setProfile(state, action) {
      const nextUser = normalizeUser(action.payload);
      const prevUser = state.user || {};

      // Some upstream payloads are partial; keep stable profile fields when absent.
      if (!nextUser.avatar && prevUser.avatar) nextUser.avatar = prevUser.avatar;
      if (!nextUser.email && prevUser.email) nextUser.email = prevUser.email;
      if (
        (!nextUser.displayName || nextUser.displayName === "Account") &&
        prevUser.displayName
      ) {
        nextUser.displayName = prevUser.displayName;
      }

      state.user = nextUser;
      state.isLoggedIn = true;
      state.isSuccess = true;
    },
    clearUser(state) {
      state.user = null;
      state.isLoggedIn = false;
    },
  },
});

export const { reset, setProfile, clearUser } = authSlice.actions;
export const authReducer = authSlice.reducer;

// --- base selectors ---
export const selectUser = (state) => state.auth.user;
export const selectIsLoggedIn = (state) => state.auth.isLoggedIn;
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectSuccess = (state) => state.auth.isSuccess;

// --- convenient derived selectors for UI ---
export const selectDisplayName = createSelector(
  [selectUser],
  (u) => u?.displayName || ""
);

export const selectEmail = createSelector([selectUser], (u) => u?.email || "");

export const selectAvatar = createSelector([selectUser], (u) => u?.avatar || "");

// useful for initials fallback
export const selectInitials = createSelector(
  [selectDisplayName, selectEmail],
  (name, email) => {
    const base = name || nameFromEmail(email) || "U";
    return base
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("");
  }
);

// (Optional) Only-first-name selector for UI
export const selectFirstName = createSelector([selectDisplayName], (name) => {
  const first = (name || "").trim().split(/\s+/)[0];
  return first || "Account";
});

